import { type CountryISOCodeUpper } from "@kazaar-platform/design-system/constants";
import { type CountryCode, parsePhoneNumber } from "libphonenumber-js";
import { isEmpty, isNil, trim } from "lodash";
import z from "zod";
import { type AddressReadDto, type MeReadDto } from "@/config/apiClient";
import { tolgee } from "@/providers/TranslationProvider";

export const addressFormSchema = z
  .object({
    id: z.string().uuid().nullable().optional(),
    name: z.string().min(1),
    recipientName: z.string().nullable().optional(),
    address: z.string().min(1),
    complementaryInfo: z.string().nullable().optional(),
    city: z.string().min(1),
    country: z.string().min(1),
    phoneCountry: z.string().min(2).max(2).nullable().optional(),
    phoneNumber: z
      .string()
      .transform((value) => trim(value))
      .nullable()
      .optional(),
    postalCode: z.string().superRefine((value, ctx) => {
      if (isNil(value) || isEmpty(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tolgee.t("inputs.requiredField"),
        });
      } else if (!/^[A-Z0-9]*$/.test(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tolgee.t("inputs.postalCode.errorInvalidString"),
        });
      }
    }),
  })
  .superRefine((address, ctx) => {
    if (
      !isNil(address.phoneNumber) &&
      !isEmpty(address.phoneNumber) &&
      !isNil(address.phoneCountry) &&
      !isEmpty(address.phoneCountry)
    ) {
      try {
        const country =
          address.phoneCountry.toUpperCase() as CountryISOCodeUpper;
        const parsedPhone = parsePhoneNumber(address.phoneNumber, country);

        if (!parsedPhone || !parsedPhone.isValid()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tolgee.t("inputs.phone.error"),
            path: ["phoneNumber"],
          });
        }
      } catch {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tolgee.t("inputs.phone.error"),
          path: ["phoneNumber"],
        });
      }
    }
  });

export type AddressFormSchema = z.infer<typeof addressFormSchema>;

export const getAddressFormDefaultValues = (
  userDefaultLang: MeReadDto["lang"] | undefined = "en",
  addressToModify?: Omit<AddressReadDto, "createdAt"> | null
) => {
  // Add a mapping country<>userLang
  let phoneCountry = (
    userDefaultLang === "en" ? "gb" : userDefaultLang
  ) as CountryCode;
  let phoneNumber = addressToModify?.phoneNumber ?? "";

  // Check if phone number exist and it is not empty to get the country code
  if (
    !isNil(addressToModify?.phoneNumber) &&
    !isEmpty(addressToModify?.phoneNumber)
  ) {
    try {
      const parsedPhone = parsePhoneNumber(addressToModify?.phoneNumber);

      if (parsedPhone?.country) {
        phoneCountry = parsedPhone.country.toLowerCase() as CountryCode;
        phoneNumber = parsedPhone.formatNational();
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error while parsing phone number", error);
      }
    }
  }

  return {
    id: addressToModify?.id ?? undefined,
    name: addressToModify?.name ?? "",
    recipientName: addressToModify?.recipientName ?? "",
    address: addressToModify?.address ?? "",
    complementaryInfo: addressToModify?.complementaryInfo ?? "",
    postalCode: addressToModify?.postalCode ?? "",
    city: addressToModify?.city ?? "",
    country: addressToModify?.country ?? "France",
    phoneCountry: phoneCountry,
    phoneNumber: phoneNumber,
  } as const satisfies AddressFormSchema;
};
